import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-2220b5a4"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "ai-chat" }
const _hoisted_2 = { class: "chat-area-container" }
const _hoisted_3 = { class: "chat-area" }
const _hoisted_4 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "disappear-zone" }, null, -1))
const _hoisted_5 = { key: 0 }
const _hoisted_6 = {
  key: 1,
  class: "no-messages"
}
const _hoisted_7 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "anchor" }, null, -1))

import acLoader from "@/components/reusable/acLoader.vue"
import AiMessageDisplayer from "./components/aiMessageDisplayer.vue"
import ChatTextarea from "./components/chatTextarea.vue"
import ChatSidebar from "./components/chatSidebar.vue"
import { useGetChatMessages, postChatMessage, createNewSession } from "@/composables/aiChatFunctions"
import { useStore } from 'vuex';
import { computed, watch, ref, onMounted, nextTick } from 'vue';
import type { ChatMessage } from '@/composables/aiChatFunctions';


export default /*#__PURE__*/_defineComponent({
  __name: 'aiChat',
  setup(__props) {


const store = useStore()
const isAIWriting = ref(false)
const selectedSession = computed(() => store.getters['ai/selectedSession']);
const userProfile = computed(() => store.getters['user/userProfile']);
const chatMessageContainer = ref(null);
const user = {
    id: userProfile.value.uid,
    email: userProfile.value.email
}

// Create refs to hold chat messages and loading state
const chatMessages = ref<ChatMessage[]>([]);
const isLoadingChatMessages = ref(false);
let cleanup = null;
const aiResponseReceivedRef = ref(false);

// Function to update chat messages when session changes
const updateChatMessages = () => {
    // Clean up previous listener if it exists
    if (cleanup) {
        cleanup();
    }
    
    // Only fetch messages if we have a valid session
    if (selectedSession.value && selectedSession.value.id) {
        const result = useGetChatMessages(
            selectedSession.value.id, 
            userProfile.value.uid
        );
        
        // Update our refs
        chatMessages.value = result.chatMessages.value;
        isLoadingChatMessages.value = result.isLoadingChatMessages.value;
        cleanup = result.cleanup;
        
        // Update the aiResponseReceived ref
        aiResponseReceivedRef.value = result.aiResponseReceived.value;
        
        // Set up a direct watcher for chatMessages to detect AI responses
        watch(result.chatMessages, (newMessages) => {
            // Check if the last message is from AI
            if (newMessages.length > 0 && newMessages[newMessages.length - 1].type === 'ai') {
                isAIWriting.value = false;
                scrollToBottom();
            }
        }, { deep: true });
    }
};

// Watch for AI response to update isAIWriting state
watch(() => aiResponseReceivedRef.value, (newValue) => {
    if (newValue) {
        isAIWriting.value = false;
        // Scroll to bottom when AI response is received
        scrollToBottom();
    }
}, { immediate: true });

// Watch for changes in chatMessages to scroll to bottom when new messages are added
watch(chatMessages, (newMessages) => {
    scrollToBottom();
    
    // Check if the last message is from AI and we're still in AI writing state
    if (newMessages.length > 0 && 
        newMessages[newMessages.length - 1].type === 'ai' && 
        isAIWriting.value) {
        isAIWriting.value = false;
    }
}, { deep: true });

// Initialize chat messages after component is mounted
onMounted( async () => {
    updateChatMessages();
    // If there is no session at all (first visit) create a new session and set it as the selected session
    if (!selectedSession.value) {
        console.log("No session found, creating a new one");
        const newSession = await createNewSession(userProfile.value.uid);
        store.dispatch('ai/setSelectedSession', newSession);
    }
});

// Watch for changes in selectedSession
watch(selectedSession, () => {
    updateChatMessages();
}, { deep: true });

const messagePosted = async (message: string) => {
    try {
        // Reset aiResponseReceived to ensure we catch the next response
        aiResponseReceivedRef.value = false;
        
        // Add user message to UI immediately
        const userMessage: ChatMessage = {
            id: 'temp-' + Date.now(), // Temporary ID that will be replaced by Firebase
            content: message,
            type: 'human',
            timestamp: new Date()
        };
        chatMessages.value.push(userMessage);
        
        // Scroll to bottom to show the new message
        scrollToBottom();
        
        // Set AI writing state
        isAIWriting.value = true;
        
        // Set a timeout to reset isAIWriting if no response is received
        const timeoutId = setTimeout(() => {
            isAIWriting.value = false;
        }, 10000); // 10 seconds timeout
        
        // Send message to backend
        await postChatMessage(message, selectedSession.value.id, user);
        
        // Clear the timeout since we're waiting for the real-time listener to update isAIWriting
        clearTimeout(timeoutId);
        
        // Set a shorter timeout to check if we need to manually reset isAIWriting
        setTimeout(() => {
            // If we're still in AI writing state after 5 seconds, check if we have an AI message
            if (isAIWriting.value && chatMessages.value.length > 0) {
                const lastMessage = chatMessages.value[chatMessages.value.length - 1];
                if (lastMessage.type === 'ai') {
                    isAIWriting.value = false;
                }
            }
        }, 5000);
        
        // AI writing state will be cleared when the real-time listener receives the AI response
    } catch (error) {
        console.error(error);
        isAIWriting.value = false;
    }
}

const handleSessionChanged = (sessionId: string) => {
    store.dispatch('ai/setSelectedSession', sessionId)
}

const scrollToBottom = async () => {
    await nextTick();

    // Use a shorter timeout to make it more responsive
    setTimeout(() => {
        if (chatMessageContainer.value) {
            chatMessageContainer.value.scrollTop = chatMessageContainer.value.scrollHeight;
            
            // Add a second scroll after a short delay to ensure it works even if content is still loading
            setTimeout(() => {
                if (chatMessageContainer.value) {
                    chatMessageContainer.value.scrollTop = chatMessageContainer.value.scrollHeight;
                }
            }, 100);
        }
    }, 50);
};


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(ChatSidebar, { onSessionChanged: handleSessionChanged }),
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", {
          ref_key: "chatMessageContainer",
          ref: chatMessageContainer,
          id: "chatMessages",
          class: "chat-messages-container"
        }, [
          _hoisted_4,
          (isLoadingChatMessages.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                _createVNode(acLoader)
              ]))
            : (chatMessages.value.length === 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, " Henüz hiç mesaj yok.Ne konuda destek almak istersiniz? "))
              : _createCommentVNode("", true),
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(chatMessages.value, (message) => {
            return (_openBlock(), _createBlock(AiMessageDisplayer, {
              key: message.id,
              collab: false,
              message: message
            }, null, 8, ["message"]))
          }), 128)),
          _hoisted_7
        ], 512)
      ]),
      _createVNode(ChatTextarea, {
        class: "!top-0",
        onMessagePosted: messagePosted,
        isAIWriting: isAIWriting.value
      }, null, 8, ["isAIWriting"])
    ])
  ]))
}
}

})